<template>
	<div
		v-if="discountCodeEnabled"
		class=""
	>
		<div class="accordion-item border-b">
			<h3>
				<button
					:aria-expanded="showDiscountCode ? 'true' : 'false'"
					aria-controls="discount-code-panel"
					id="discount-code-header"
					class="flex items-center justify-between w-full py-4 cursor-pointer"
					@click="handleDiscountCodeToggle"
					@keydown="handleKeyDown"
				>
			<div class="flex items-center gap-2 justify-between w-[calc(100%-1rem)] text-left flex-wrap">
				<span class="flex gap-2 relative items-center">
					<span
						v-if="showAccordionIcons"
						class="mini-cart__util-icon"
					>
						<MiniCartIconDiscount />
					</span>						
					{{ discountCodeLabel || 'Add discount code' }}
				</span>
					<span
						v-if="appliedCodes.length && !showDiscountCode"
						class="mini-cart__truncated-preview-text"
					>
						{{ discountAppliedLabel }}
					</span>
					</div>
					<svg
						:class="showDiscountCode ? 'rotate-180' : 'rotate-0'"
						class="w-4 h-4 transition-transform duration-200"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M19 9l-7 7-7-7"
						></path>
					</svg>
				</button>
			</h3>
			<button
        v-if="showDiscountCode"
        @click="handleDiscountCodeToggle"
        class="w-full h-full fixed inset-0 mini-cart__blocker"
        style="
          background-color: var(--global-mask-color);
          backdrop-filter: var(--global-mask-blur);
        "
      >
        <span class="sr-only">close</span>
      </button>
			<div
				id="discount-code-panel"
				aria-labelledby="discount-code-header"
				class="overflow-hidden transition-all duration-300 ease-in-out mini-cart__slide-up-panel"
				:class="showDiscountCode ? 'max-h-96 opacity-100 panel_active' : 'panel_disabled'"
				@keydown="handlePanelKeyDown"
			>
				<div class="flex justify-between items-center mb-2">
					<h3
						id="mini-cart__discount-code-form-label"
						class="">
						{{ discountCodeLabel || 'Add discount code' }}
					</h3>
					<button
						class="w-11 h-11 right-0 flex items-center justify-center"
						@click.passive="toggleDiscountCode">
						<MiniCartIconClose />
						<span class="sr-only">Close</span>
					</button>
				</div>
				<form
					class="discount-code-form relative transition-all duration-300 ease-in-out pb-4"
					:class="showDiscountCode ? 'opacity-100' : 'opacity-0'"
					@submit.prevent="applyDiscountCode"
					aria-labelledby="mini-cart__discount-code-form-label"
				>
					<fieldset>
						<div class="flex gap-2 items-center">
							<label for="mini-cart__discount-code-input" class="sr-only">
								{{ discountCodePlaceholder || 'Enter discount code' }}
							</label>
							<input
								id="mini-cart__discount-code-input"
								type="text"
								class="flex-1"
								:placeholder="discountCodePlaceholder || 'Enter code'"
								v-model="discountCodeInput"
								ref="discountInput"
								name="discount"
							>
							<button
								type="submit"
								class="mini-cart__button"
								:disabled="!discountCodeInput.trim() || isApplying"
							>
								{{ isApplying ? discountCodeApplyingText : discountCodeApplyButton }}
							</button>
						</div>

						<!-- Applied discount codes -->
						<div
							v-if="appliedCodes.length"
							class="mt-4"
						>
							<p class="text-xs mb-2">{{ appliedDiscountsHeading }}</p>
							<div class="space-y-2">
								<div
									v-for="code in appliedCodes"
									:key="code"
									class="flex items-center justify-between bg-green-50 border border-green-200 rounded p-2"
								>
									<span class="text-xs text-green-800">{{ code }}</span>
									<button
										type="button"
										@click="removeDiscountCode(code)"
										class="text-green-600 hover:text-green-800 text-xs underline"
									>
										{{ removeDiscountText || 'Remove' }}
									</button>
								</div>
							</div>
						</div>

						<!-- Error message -->
						<div
							v-if="errorMessage"
							class="mt-2 text-red-600 text-xs"
						>
							{{ errorMessage }}
						</div>
					</fieldset>
				</form>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useAccordion } from './composables/useAccordion';
import MiniCartIconClose from './mini-cart--icon-close.vue';
import MiniCartIconDiscount from './mini-cart--icon-discount.vue';
// Props
const props = defineProps({
	cartData: {
		type: Object,
		default: () => ({})
	}
});

// Emits
const emit = defineEmits(['cart-updated']);

// Discount Code Vars
const discountCodeEnabled = computed(() => window.miniCartCustomSettings.discountCodeEnabled); // Default to true
const discountCodeLabel = computed(() => window.miniCartCustomSettings.discountCodeLabel || 'Add discount code');
const discountCodePlaceholder = computed(() => window.miniCartCustomSettings.discountCodePlaceholder || 'Enter code');
const removeDiscountText = computed(() => window.miniCartCustomSettings.removeDiscount || 'Remove discount');
const discountCodeApplyingText = computed(() => window.miniCartCustomSettings.discountCodeApplyingText || 'Applying...');
const discountCodeApplyButton = computed(() => window.miniCartCustomSettings.discountCodeApplyButton || 'Apply');
const appliedDiscountsHeading = computed(() => window.miniCartCustomSettings.appliedDiscountsHeading || 'Applied Discounts:');
const discountAppliedLabelTemplate = computed(() => window.miniCartCustomSettings.discountAppliedLabelTemplate || '({codes}) Discount Applied');
const showAccordionIcons = ref(window.miniCartCustomSettings?.showAccordionIcons ?? true);

// Generate the discount applied label with actual codes
const discountAppliedLabel = computed(() => {
	const codesString = appliedCodes.value.join(', ');
	return discountAppliedLabelTemplate.value.replace(/{codes}/g, codesString);
});

// const showDiscountCode = ref(false);
const { isOpen: showDiscountCode, toggle: toggleDiscountCode, close: closeDiscountCode } = useAccordion('discount-code');
const discountCodeInput = ref('');
const isApplying = ref(false);
const errorMessage = ref('');
const appliedCodes = ref([]);

// Refs
const discountInput = ref(null);

// Handle accordion toggle
const handleDiscountCodeToggle = () => {
	toggleDiscountCode();
	console.log("Discount code accordion toggled. Current value:", showDiscountCode.value);
};

// Handle keyboard events for accordion header
function handleKeyDown(event) {
	if (event.key === 'Enter' || event.key === ' ') {
		event.preventDefault();
		handleDiscountCodeToggle();
	}
}

// Handle Escape key in accordion panel
function handlePanelKeyDown(event) {
	if (event.key === 'Escape') {
		closeDiscountCode();
		// Return focus to header button
		const headerButton = document.getElementById('discount-code-header');
		if (headerButton) {
			headerButton.focus();
		}
	}
}

// Apply discount code
async function applyDiscountCode() {
	if (!discountCodeInput.value.trim()) return;
	
	console.log("Applying discount code:", discountCodeInput.value);
	isApplying.value = true;
	errorMessage.value = '';
	
	try {
		// Get existing discount codes
		let discountCodes = getStoredDiscountCodes();
		const newCode = discountCodeInput.value.trim();
		
		// Add new code if not already applied
		if (!discountCodes.includes(newCode)) {
			discountCodes.push(newCode);
		}
		
		const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ discount: discountCodes.join(',') })
		});
		
		const data = await response.json();
		
		// Filter valid codes from the response
		const validCodes = data.discount_codes.filter(code => code.applicable).map(code => code.code);
		const newlyInvalidCodes = data.discount_codes
			.filter(code => !code.applicable && discountCodes.includes(code.code))
			.map(code => code.code);
		
		if (newlyInvalidCodes.length > 0) {
			errorMessage.value = `Failed to apply discount code: ${newlyInvalidCodes.join(', ')}`;
			console.warn(errorMessage.value);
		} else {
			// Clear input if successful
			discountCodeInput.value = '';
		}
		
		// Update stored codes and applied codes display
		setStoredDiscountCodes(validCodes);
		appliedCodes.value = validCodes;
		
		emit('cart-updated', data);
		
	} catch (error) {
		console.error('Failed to apply discount code:', error);
		errorMessage.value = 'Failed to apply discount code. Please try again.';
	} finally {
		isApplying.value = false;
	}
}

// Remove discount code
async function removeDiscountCode(codeToRemove) {
	console.log("Removing discount code:", codeToRemove);
	
	try {
		let discountCodes = getStoredDiscountCodes();
		discountCodes = discountCodes.filter(code => code !== codeToRemove);
		
		const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ discount: discountCodes.join(',') })
		});
		
		const data = await response.json();
		
		// Update stored codes and applied codes display
		setStoredDiscountCodes(discountCodes);
		appliedCodes.value = discountCodes;
		
		emit('cart-updated', data);
		
	} catch (error) {
		console.error('Failed to remove discount code:', error);
		errorMessage.value = 'Failed to remove discount code. Please try again.';
	}
}

// Helper functions for localStorage management
function getStoredDiscountCodes() {
	try {
		const stored = localStorage.getItem('appliedDiscountCodes');
		const dateStored = localStorage.getItem('discountCodeAddedDate');
		
		if (!stored) return [];

		if (dateStored) {
			const addedDate = new Date(dateStored);
			const currentDate = new Date();
			// const hoursDiff = (currentDate - addedDate) / 1000;
			const hoursDiff = (currentDate - addedDate) / (1000 * 60 * 60);
			if (hoursDiff > 48) {
				console.log('Discount codes expired (older than 10 seconds), clearing...');
				localStorage.removeItem('appliedDiscountCodes');
				localStorage.removeItem('discountCodeAddedDate');
				return [];
			}
		}
		
		return JSON.parse(stored);
	} catch (error) {
		console.error('Error reading discount codes from localStorage:', error);
		return [];
	}
}

function setStoredDiscountCodes(codes) {
	try {
		if (codes.length > 0) {
			localStorage.setItem('appliedDiscountCodes', JSON.stringify(codes));
			// Only set the date if it doesn't exist (preserve original date)
			if (!localStorage.getItem('discountCodeAddedDate')) {
				localStorage.setItem('discountCodeAddedDate', new Date().toISOString());
			}
		} else {
			localStorage.removeItem('appliedDiscountCodes');
			localStorage.removeItem('discountCodeAddedDate');
		}
	} catch (error) {
		console.error('Error storing discount codes to localStorage:', error);
	}
}

async function checkAndClearExpiredCodes() {
	const dateStored = localStorage.getItem('discountCodeAddedDate');
	
	if (dateStored) {
		const addedDate = new Date(dateStored);
		const currentDate = new Date();
		// const hoursDiff = (currentDate - addedDate) / 1000;
		const hoursDiff = (currentDate - addedDate) / (1000 * 60 * 60);
		if (hoursDiff > 48) {
			localStorage.removeItem('appliedDiscountCodes');
			localStorage.removeItem('discountCodeAddedDate');
			appliedCodes.value = [];
			try {
				const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ discount: '' })
				});
				
				const data = await response.json();
				emit('cart-updated', data);
			} catch (error) {
				console.error('Failed to clear expired discount codes from cart:', error);
			}
		}
	}
}

// Watch for cart data changes from parent
watch(() => props.cartData, async (newCartData) => {
	if (newCartData) {
		console.log('Cart data watcher - discount codes:', newCartData.discount_codes);
		await checkAndClearExpiredCodes();
		
		// Handle discount codes from cart data
		if (newCartData.discount_codes) {
			const validCodes = newCartData.discount_codes
				.filter(code => code.applicable)
				.map(code => code.code);
			appliedCodes.value = validCodes;
			setStoredDiscountCodes(validCodes);
		}
		
		// Also handle cart_level_discount_applications for display
		if (newCartData.cart_level_discount_applications) {
			const discountTitles = newCartData.cart_level_discount_applications.map(app => app.title);
			// Merge with existing codes to show all applied discounts
			const allCodes = [...new Set([...appliedCodes.value, ...discountTitles])];
			appliedCodes.value = allCodes;
		}
		
		// NEVER auto-open accordion - always start closed and let user manually open
		console.log('Cart data loaded - discount accordion remains in current state:', showDiscountCode.value);
	}
}, { immediate: true });

// Initialize applied codes on mount
(async () => {
	await checkAndClearExpiredCodes();
	appliedCodes.value = getStoredDiscountCodes();
})();

// Expose methods that parent might need
defineExpose({
	applyDiscountCode,
	removeDiscountCode,
	discountCodeInput
});
</script>
